import React from 'react';
import {
  Modal,
  Button,
  ModalVariant,
  Switch,
  Card,
  CardTitle,
  CardBody,
  Checkbox,
  Tabs,
  Tab,
} from '@patternfly/react-core';
import { Table, Thead, Tbody, Tr, Th, Td } from '@patternfly/react-table';
import { observer } from 'mobx-react';
import { withErrorBoundary } from 'react-error-boundary';
import useMobxStores from 'hooks/useMobxStores';
import ComponentError from 'components/Error/ComponentError';
import { handleError } from 'services/ErrorService';
import { FeatureFlag, FeatureSection } from 'types';
import { useFeatureFlagManager } from './context';

import './styles.scss';

const formatConfigValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2);
  }
  return String(value);
};

const FeatureFlagManagerModal = observer(() => {
  const { featuresStore, configStore } = useMobxStores();
  const { isModalOpen, closeModal, closeWhenToggled, setCloseWhenToggled } =
    useFeatureFlagManager();
  const [activeTabKey, setActiveTabKey] = React.useState<string | number>(
    'enablements',
  );

  const handleTabChange = (
    _event: React.MouseEvent,
    tabKey: string | number,
  ) => {
    setActiveTabKey(tabKey);
  };

  const handleChange = (section: FeatureSection, changedFlag: FeatureFlag) => {
    const newSection = {
      ...section,
      flags: section.flags.map((ff) => {
        if (changedFlag.name === ff.name) {
          return {
            ...ff,
            active: !ff.active,
          };
        }

        return ff;
      }),
    };
    featuresStore.setAFeature(newSection);

    if (closeWhenToggled) {
      closeModal();
    }
  };

  const handleAutoCloseChange = (
    _event: React.FormEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    setCloseWhenToggled(checked);
  };

  const handleReset = () => {
    featuresStore.getAllFeatures();
  };

  const sortedSections = Array.isArray(featuresStore.features)
    ? [...featuresStore.features].sort((a, b) =>
        a.section.localeCompare(b.section),
      )
    : [];

  const enablementSection = sortedSections.find(
    (section) => section.section === 'enablements',
  );
  const sortedEnablementFlags = enablementSection?.flags
    ? [...enablementSection.flags].sort((a, b) => a.name.localeCompare(b.name))
    : [];
  const legacySections = sortedSections.filter(
    (section) => section.section !== 'enablements',
  );

  const oleConfigEntries = Object.entries(configStore.config || {}).sort(
    ([keyA], [keyB]) => keyA.localeCompare(keyB),
  );

  if (!isModalOpen) {
    return null;
  }

  return (
    <Modal
      title="Feature flag manager"
      isOpen={isModalOpen}
      variant={ModalVariant.large}
      onClose={closeModal}
      actions={[
        <Button key="confirm" variant="danger" onClick={closeModal}>
          Close
        </Button>,
        <Button key="reset" variant="tertiary" onClick={handleReset}>
          Reset all changes to API values
        </Button>,
      ]}
    >
      <p>
        You can use the toggles below to <b>override</b> feature flag values
        directly on the client-side. The purpose of this tool is to simulate the
        feature flag values in the UI for testing purposes.
      </p>
      <p>
        <i>
          Keep in mind that even though this list is obtained from the API,
          these overrides are not saved to the database by this manager and will
          be lost when the page is refreshed. This tool is available only for
          local development or super users.
        </i>
      </p>
      <p>
        <b>Shortcut</b>: Control + Space
      </p>
      <div className="feature-flag-manager__checkbox">
        <Checkbox
          label="Close modal when feature is toggled"
          isChecked={closeWhenToggled}
          onChange={handleAutoCloseChange}
          id="close_modal_when_toggled"
        />
      </div>

      <Tabs activeKey={activeTabKey} onSelect={handleTabChange} isBox isFilled>
        <Tab eventKey="enablements" title="Enablement feature flags">
          <div className="feature-flag-manager__tab-content">
            {enablementSection ? (
              <div className="feature-flag-manager__ff-list">
                {sortedEnablementFlags.map((ff) => (
                  <div className="feature-flag-manager__ff" key={ff.name}>
                    <Switch
                      label={ff.name}
                      isChecked={ff.active}
                      onChange={() => handleChange(enablementSection, ff)}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <p>No enablement feature flags were found.</p>
            )}
          </div>
        </Tab>
        <Tab eventKey="legacy" title="Legacy feature flags">
          <div className="feature-flag-manager__tab-content feature-flag-manager__list">
            {legacySections.map((ffSection) => (
              <Card key={ffSection.section}>
                <CardTitle>
                  <h4>Section: {ffSection.section}</h4>
                </CardTitle>

                <CardBody className="feature-flag-manager__ff-list">
                  {ffSection.flags?.map((ff) => (
                    <div className="feature-flag-manager__ff" key={ff.name}>
                      <Switch
                        label={ff.name}
                        isChecked={ff.active}
                        onChange={() => handleChange(ffSection, ff)}
                      />
                    </div>
                  ))}
                </CardBody>
              </Card>
            ))}
          </div>
        </Tab>
        <Tab eventKey="ole_config" title="OLE Config">
          <div className="feature-flag-manager__tab-content">
            <p>
              Read-only view of the server config currently loaded by the client
              (<code>configStore</code>, sourced from ole_config).
            </p>
            {oleConfigEntries.length > 0 ? (
              <Table aria-label="OLE config" variant="compact">
                <Thead>
                  <Tr>
                    <Th width={30}>Key</Th>
                    <Th>Value</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {oleConfigEntries.map(([key, value]) => (
                    <Tr key={key}>
                      <Td dataLabel="Key">
                        <code>{key}</code>
                      </Td>
                      <Td dataLabel="Value">
                        <pre className="feature-flag-manager__config-value">
                          {formatConfigValue(value)}
                        </pre>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            ) : (
              <p>No OLE config has been loaded yet.</p>
            )}
          </div>
        </Tab>
      </Tabs>
    </Modal>
  );
});

export default withErrorBoundary(
  FeatureFlagManagerModal,
  ComponentError,
  handleError,
);
